/**
 * LandingPage — premium public homepage for the Omni workspace platform.
 *
 * Scopes the entire page under the `.landing` wrapper, importing the
 * premium design tokens and layout rules from `landing.css`.
 * Composes 12 high-end, visual-first subcomponents.
 */
import * as React from "react";
import { LandingNav } from "@/components/landing/LandingNav";
import { HeroSection, StatsRibbon } from "@/components/landing/HeroSection";
import { CapabilitiesGrid } from "@/components/landing/CapabilitiesGrid";
import { AudienceSwitcher } from "@/components/landing/AudienceSwitcher";
import { DeveloperSection } from "@/components/landing/DeveloperSection";
import { EnterpriseSection } from "@/components/landing/EnterpriseSection";
import { PricingGrid } from "@/components/landing/PricingGrid";
// Testimonials hidden until we have real, attributable quotes. The
// component still exists for re-enable later; leaving the import
// commented makes the lint reason explicit instead of looking
// accidentally removed.
// import { TestimonialsGrid } from "@/components/landing/TestimonialsGrid";
import { FAQSection } from "@/components/landing/FAQSection";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { LandingFooter } from "@/components/landing/LandingFooter";

import "@/components/landing/landing.css";

export function LandingPage() {
  const [theme, setTheme] = React.useState<"dark" | "light">("dark");

  React.useEffect(() => {
    document.title = "Omni — One brain. Every device. Always on.";
  }, []);

  const handleThemeToggle = React.useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <div className="landing" data-theme={theme}>
      <LandingNav onThemeToggle={handleThemeToggle} theme={theme} />
      <main>
        <HeroSection />
        <StatsRibbon />
        <CapabilitiesGrid />
        <AudienceSwitcher />
        <DeveloperSection />
        <EnterpriseSection />
        <PricingGrid />
        {/* <TestimonialsGrid /> — re-enable when we have real quotes */}
        <FAQSection />
        <FinalCTA />
        <LandingFooter />
      </main>
    </div>
  );
}
